import React, { ReactNode, useRef, useState } from 'react';
import styles from './CustomSlider.module.css';

export interface CustomSliderProps {
  value: number;
  min?: number;
  max?: number;
  step?: number;
  title?: string;
  tooltipFormatter?: (value: number) => ReactNode;
  onChange: (value: number) => void;
  disabled?: boolean;
  style?: React.CSSProperties;
}

// Main Slider used to replace antd Slider across the app, with custom tooltip and styling. Can be used for any generic slider use case.
const CustomSlider: React.FC<CustomSliderProps> = ({
  value,
  title = 'Slider',
  min = 0,
  max = 100,
  step = 1,
  tooltipFormatter,
  onChange,
  disabled = false,
  style = {},
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const sliderRef = useRef<HTMLInputElement>(null);

  // Calculate thumb position for tooltip
  const getThumbPosition = () => {
    if (sliderRef.current) {
      const range = Math.max(max - min, 1);
      const percent = (value - min) / range;
      const sliderWidth = sliderRef.current.clientWidth;
      const thumbWidth = 18; // match CSS
      return percent * (sliderWidth - thumbWidth) + thumbWidth / 2;
    }
    return '50%';
  };

  return (
    <div
      className="w-100"
      style={{ position: 'relative', width: style.width, ...style }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <input
        ref={sliderRef}
        type="range"
        className={`w-100 rounded-2 cursor-pointer ${styles.whiteSlider}`}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        title={title}
        disabled={disabled}
        style={{
          ['--slider-bg' as any]: `linear-gradient(90deg, #f5f5f5 ${((value - min) / Math.max(max - min, 1)) * 100}%, #222 ${((value - min) / Math.max(max - min, 1)) * 100}%)`,
          padding: 0,
        }}
      />
      <div
        className={`${styles.sliderTooltip} ${showTooltip ? styles.sliderTooltipVisible : ''}`}
        style={{
          left:
            typeof getThumbPosition() === 'number' ? `${getThumbPosition()}px` : getThumbPosition(),
        }}
      >
        {typeof tooltipFormatter === 'function' ? tooltipFormatter(value) : value}
        <span className={styles.sliderTooltipArrow} />
      </div>
    </div>
  );
};

export default CustomSlider;
